import React from 'react';
import { Box, TextField, Button, Typography, CircularProgress, useTheme } from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';

interface CustomerFormValues {
    firstName: string;
    lastName: string;
    email: string;
}

interface CustomerFormProps {
    onSubmit: (values: CustomerFormValues) => Promise<void>;
}

const validationSchema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Enter a valid email').required('Email is required'),
});

const initialValues: CustomerFormValues = {
    firstName: '',
    lastName: '',
    email: '',
};

const CustomerForm: React.FC<CustomerFormProps> = ({ onSubmit }) => {
    const theme = useTheme();

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    await onSubmit(values);
                } catch (error) {
                    console.error('Form submission error:', error);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ isSubmitting, touched, errors, handleChange, handleBlur, values, handleSubmit }) => (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        width: '100%',
                        maxWidth: 500,
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    <Typography variant="h6" align="center" gutterBottom>
                        Enter your details to get your coupon
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            id="firstName"
                            name="firstName"
                            label="First Name"
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.firstName && Boolean(errors.firstName)}
                            helperText={touched.firstName && errors.firstName}
                            disabled={isSubmitting}
                        />

                        <TextField
                            fullWidth
                            id="lastName"
                            name="lastName"
                            label="Last Name"
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.lastName && Boolean(errors.lastName)}
                            helperText={touched.lastName && errors.lastName}
                            disabled={isSubmitting}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        disabled={isSubmitting}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={isSubmitting}
                        sx={{
                            mt: 2,
                            py: 1.5,
                            fontSize: '1.1rem',
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Get Your Coupon'
                        )}
                    </Button>
                </Box>
            )}
        </Formik>
    );
};

export default CustomerForm;
